// Shared loading placeholder for widgets fetching from app-api.
//
// While `useWidgetData` is pending, a widget shows a faint outline of what's
// coming — shaped by its module TYPE (skeletonShapes.ts; the type is
// "skeleton" in modules.json, looked up from the module's title), with a
// shimmer sweeping across it, each piece a beat after the one before (after
// catalyst-bento's LoadState skeletons). When the data lands the skeleton
// unmounts and the module body gets `.bm-revealed` for a moment: the content
// fades up (styles.css). Only ever shown pre-data, so VRT baselines (which
// screenshot the loaded state) don't move. Styling: `.bm-skeleton` / `.skel-*`.

import { createContext, use, useLayoutEffect, useRef } from "react";
import { skeletonHTML, skeletonKindFor, type SkeletonKind } from "./skeletonShapes.js";

/** The module title a widget is rendering for (provided by ModuleBody). */
export const ModuleTitleContext = createContext<string | null>(null);

export interface WidgetSkeletonProps {
  /** Override the module's own skeleton type (modules.json "skeleton"). */
  kind?: SkeletonKind;
  /** Number of placeholder rows, for the row-based kinds. */
  rows?: number;
}

const REVEAL_MS = 600;

export default function WidgetSkeleton({ kind, rows }: WidgetSkeletonProps) {
  const title = use(ModuleTitleContext);
  const k = kind ?? skeletonKindFor(title);
  const ref = useRef<HTMLDivElement>(null);
  // On unmount (= the data arrived), fade the real content up.
  useLayoutEffect(() => {
    const body = ref.current?.closest<HTMLElement>(".module-body");
    return () => {
      if (!body || !body.isConnected) return;
      body.classList.remove("bm-revealed");
      void body.offsetWidth; // restart if it's still running
      body.classList.add("bm-revealed");
      window.setTimeout(() => body.classList.remove("bm-revealed"), REVEAL_MS);
    };
  }, []);
  return (
    <div
      ref={ref}
      className={`data-wrapper top-align bm-skeleton skel-${k}`}
      aria-busy="true"
      dangerouslySetInnerHTML={{ __html: skeletonHTML(k, rows) }}
    />
  );
}
